import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useCart from '../../../data/useCart'
import useCheckoutContext from '../../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../../lib/get-phoenix-url-params'
import { PoliciesInitialValues } from '../../../typings/AdditionalInformation'
import SkeletonPolicies from '../Skeletons/SkeletonPolicies'

import {
  PoliciesCartDocument,
  PoliciesCartQuery,
  PoliciesSetPoliciesOnCartDocument,
  PoliciesSetPoliciesOnCartMutation,
  PoliciesSetPoliciesOnCartMutationVariables,
} from '../../../graphql'
import PoliciesComponent from './PoliciesComponent'

const Policies: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const { cartId } = getPhoenixUrlParams(location.search)

  const [initialValues, setInitialValues] = useState<PoliciesInitialValues>()

  const { cartData, isCartLoading } = useCart<PoliciesCartQuery>(PoliciesCartDocument)

  const [setPoliciesOnCart, { data: setPoliciesOnCartData, loading: setPoliciesOnCartLoading }] =
    useMutationWithErrorHandling<PoliciesSetPoliciesOnCartMutation, PoliciesSetPoliciesOnCartMutationVariables>(
      PoliciesSetPoliciesOnCartDocument,
      {},
    )

  useEffect(() => {
    if (setPoliciesOnCartLoading || !setPoliciesOnCartData) {
      context.setIsPoliciesValid(false)
    } else if (setPoliciesOnCartData && setPoliciesOnCartData.setPoliciesOnCart.__typename === 'Cart') {
      // Set the policies validity to true after we get back the result from the mutation
      context.setIsPoliciesValid(true)
    }
  }, [setPoliciesOnCartLoading, setPoliciesOnCartData])

  useEffect(() => {
    if (!cartData) return

    const policiesInitialValues = cartData.cart.policiesInput || []

    setInitialValues({ policies: policiesInitialValues })

    const mandatoryPolicies = cartData.cart.policies.map((policy) => policy.mandatory)
    if (policiesInitialValues.length === mandatoryPolicies.length) {
      context.setIsPoliciesValid(true)
    }
  }, [cartData])

  if (!cartData || isCartLoading || !initialValues) {
    return <SkeletonPolicies />
  }

  // Render nothing if we don't have policies to accept
  if (cartData.cart.policies.length === 0) {
    context.setIsPoliciesValid(true)
    return null
  }

  const { venue, policies } = cartData.cart

  const handleChange = (values: PoliciesInitialValues) => {
    const mandatoryPolicies = policies.map((policy) => policy.mandatory)

    if (values.policies.length === mandatoryPolicies.length) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      setPoliciesOnCart({ variables: { cartId: cartId as string, policies: values.policies } })
      context.setIsPoliciesValid(true)
    } else {
      context.setIsPoliciesValid(false)
    }
  }

  return (
    <PoliciesComponent
      venueName={venue.name}
      policies={policies}
      initialValues={initialValues}
      handleChange={handleChange}
    />
  )
}

export default Policies
